import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import {
  newsletterSchema,
  type NewsletterFormData,
} from '@/features/noticias/schemas/newsletter.schema';
import { useSuscribirNewsletterMutation } from '@/features/noticias/queries/newsletter.queries';

export function NewsletterForm(): React.ReactElement {
  const suscribirMutation = useSuscribirNewsletterMutation();

  const form = useForm<NewsletterFormData>({
    resolver: zodResolver(newsletterSchema),
    defaultValues: { email: '' },
  });

  function onSubmit(data: NewsletterFormData): void {
    suscribirMutation.mutate(data, {
      onSuccess: response => {
        toast.success(response.message);
        form.reset();
      },
      onError: () => {
        toast.error('No pudimos registrar tu suscripción. Por favor intenta nuevamente.');
      },
    });
  }

  const emailError = form.formState.errors.email?.message;

  return (
    <div className="border-border/50 bg-card/40 shadow-strong rounded-2xl border p-6 backdrop-blur-2xl">
      <div className="mx-auto max-w-2xl text-center">
        <h3 className="text-foreground mb-3 text-xl font-bold md:text-2xl">
          ¿No quieres perderte nada?
        </h3>
        <p className="text-muted-foreground mb-5 text-sm">
          Suscríbete a nuestro newsletter y recibe las últimas noticias directamente en tu bandeja
          de entrada.
        </p>
        <form onSubmit={form.handleSubmit(onSubmit)} noValidate>
          <div className="flex flex-col justify-center gap-3 sm:flex-row">
            <input
              type="email"
              placeholder="Tu email"
              aria-label="Email para el newsletter"
              aria-invalid={emailError ? true : undefined}
              disabled={suscribirMutation.isPending}
              {...form.register('email')}
              className="border-border/70 bg-card/60 shadow-medium hover:shadow-strong focus:border-primary focus:bg-card/80 focus:ring-primary/50 flex-1 rounded-xl border-2 px-4 py-2 text-sm backdrop-blur-md transition-all duration-300 focus:ring-2 focus:outline-none disabled:opacity-60"
            />
            <button
              type="submit"
              disabled={suscribirMutation.isPending}
              className="from-primary to-primary/80 text-primary-foreground shadow-medium hover:shadow-strong inline-flex items-center justify-center rounded-xl bg-linear-to-r px-6 py-2 text-sm font-semibold transition-all duration-300 hover:-translate-y-0.5 disabled:pointer-events-none disabled:opacity-60"
            >
              {suscribirMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Suscribirse
            </button>
          </div>
          {emailError && (
            <p role="alert" className="text-destructive mt-2 text-left text-sm">
              {emailError}
            </p>
          )}
        </form>
      </div>
    </div>
  );
}
